import React, { useState, useEffect } from 'react';
import { X, MapPin, Loader2 } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import Dropdown from '../../components/Dropdown';

const formatDateTimeLocal = (dateVal) => {
  if (!dateVal) return '';
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  const year = d.getFullYear();
  const month = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const hours = pad(d.getHours());
  const minutes = pad(d.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const AddDelivery = ({ onClose, onSuccess, delivery }) => {
  const [formData, setFormData] = useState({
    leadId: '',
    title: '',
    deliveryBoyId: '',
    status: 'Pending',
    scheduledDate: '',
    details: '',
    orderNo: '',
    orderDate: '',
    amount: '',
    partyName: '',
    partyPhone: '',
    partyEmail: '',
    partyAddress: '',
    partyCity: '',
    partyState: '',
    partyPincode: '',
    latitude: '',
    longitude: ''
  });
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [statusOptions, setStatusOptions] = useState([
    { value: 'Pending', label: 'Pending' },
    { value: 'In Progress', label: 'In Progress' },
    { value: 'Delivered', label: 'Delivered' },
    { value: 'Cancelled', label: 'Cancelled' }
  ]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (delivery) {
      let latVal = delivery.latitude !== null && delivery.latitude !== undefined ? delivery.latitude : '';
      let lngVal = delivery.longitude !== null && delivery.longitude !== undefined ? delivery.longitude : '';
      let rawAddr = delivery.partyAddress || delivery.lead?.address || '';
      
      if ((!latVal || !lngVal) && rawAddr) {
        const gpsMatch = rawAddr.match(/\[GPS:\s*Lat:\s*([\d.-]+),\s*Lon:\s*([\d.-]+)\]/i);
        if (gpsMatch) {
          latVal = gpsMatch[1];
          lngVal = gpsMatch[2];
        }
      }
      const cleanAddress = rawAddr ? rawAddr.replace(/\s*\[GPS:\s*Lat:\s*[\d.-]+,\s*Lon:\s*[\d.-]+\]/gi, '').trim() : '';

      setFormData({
        leadId: delivery.leadId || '',
        title: delivery.title || '',
        deliveryBoyId: delivery.deliveryBoyId || '',
        status: delivery.status || 'Pending',
        scheduledDate: delivery.scheduledDate ? formatDateTimeLocal(delivery.scheduledDate) : '',
        details: delivery.details || '',
        orderNo: delivery.orderNo || '',
        orderDate: delivery.orderDate ? new Date(delivery.orderDate).toISOString().split('T')[0] : '',
        amount: delivery.amount !== undefined && delivery.amount !== null ? delivery.amount : '',
        partyName: delivery.partyName || delivery.lead?.companyName || '',
        partyPhone: delivery.partyPhone || delivery.lead?.phone || '',
        partyEmail: delivery.partyEmail || delivery.lead?.email || '',
        partyAddress: cleanAddress,
        partyCity: delivery.partyCity || delivery.lead?.city || '',
        partyState: delivery.partyState || delivery.lead?.state || '',
        partyPincode: delivery.partyPincode || delivery.lead?.pincode || '',
        latitude: latVal,
        longitude: lngVal
      });
    }
  }, [delivery]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [leadsRes, usersRes, statusRes] = await Promise.all([
          api.get('/leads?limit=1000'),
          api.get('/auth/users?page=1&limit=1000').catch(() => ({ data: [] })),
          api.get('/masters/delivery-statuses').catch(() => ({ data: [] }))
        ]);
        
        if (leadsRes.data && leadsRes.data.data) {
          setLeads(leadsRes.data.data);
        } else if (Array.isArray(leadsRes.data)) {
          setLeads(leadsRes.data);
        }
        
        if (usersRes.data && usersRes.data.data) {
          const allUsers = usersRes.data.data;
          setUsers(allUsers.filter(u => {
            const roleName = (u.role?.name || u.role || '').toString().toLowerCase();
            return roleName === 'delivery boy';
          }));
        } else if (Array.isArray(usersRes.data)) {
          const allUsers = usersRes.data;
          setUsers(allUsers.filter(u => {
            const roleName = (u.role?.name || u.role || '').toString().toLowerCase();
            return (roleName === 'delivery boy' || roleName.includes('delivery')) || true;
          }));
        }

        const rawStatuses = statusRes.data?.data || (Array.isArray(statusRes.data) ? statusRes.data : []);
        const activeStatuses = rawStatuses
          .filter(s => s.status === 'Active')
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        if (activeStatuses.length > 0) {
          setStatusOptions(activeStatuses.map(s => ({ value: s.name, label: s.name })));
        }
      } catch (err) {
        console.error("Failed to load options", err);
        toast.error("Failed to load leads or users");
      }
    };
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDropdownChange = (name, value) => {
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'leadId' && value) {
        const selectedLead = leads.find(l => String(l.id) === String(value));
        if (selectedLead) {
          updated.partyName = selectedLead.companyName || selectedLead.contactPerson || '';
          updated.partyPhone = selectedLead.phone || '';
          updated.partyEmail = selectedLead.email || '';
          updated.partyAddress = selectedLead.address || '';
          updated.partyCity = selectedLead.city || '';
          updated.partyState = selectedLead.state || '';
          updated.partyPincode = selectedLead.pincode || '';
          if (selectedLead.latitude && selectedLead.longitude) {
            updated.latitude = selectedLead.latitude;
            updated.longitude = selectedLead.longitude;
          }
        }
      }
      return updated;
    });
  };

  

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.partyName?.trim()) {
      return toast.error("Please provide a Party / Customer Name");
    }
    if (!formData.title?.trim()) {
      return toast.error("Please provide a Delivery Title / Item Name");
    }
    if (!formData.partyEmail?.trim()) {
      return toast.error("Please provide an Email Address");
    }
    if (!formData.partyAddress?.trim()) {
      return toast.error("Please provide a Delivery Address");
    }
    if (!formData.deliveryBoyId) {
      return toast.error("Please assign a Delivery Boy");
    }
    if (!formData.scheduledDate) {
      return toast.error("Please select a Scheduled Date & Time");
    }
    if (formData.partyEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.partyEmail.trim())) {
      return toast.error("Please provide a valid Email Address");
    }
    if (formData.partyPhone && formData.partyPhone.trim().length !== 10) {
      return toast.error("Phone number must be exactly 10 digits");
    }
    
    try {
      setLoading(true);
      const payload = {
        ...formData,
        leadId: formData.leadId ? Number(formData.leadId) : null,
        deliveryBoyId: formData.deliveryBoyId ? Number(formData.deliveryBoyId) : null,
        latitude: formData.latitude !== '' && formData.latitude !== null ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude !== '' && formData.longitude !== null ? parseFloat(formData.longitude) : null
      };

      if (delivery) {
        await api.put(`/deliveries/${delivery.id}`, payload);
        toast.success("Delivery updated");
      } else {
        await api.post('/deliveries', payload);
        toast.success("Delivery created");
      }
      onSuccess();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="modal-content" style={{ backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '850px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
        <div className="modal-header" style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f9fafb', borderTopLeftRadius: '12px', borderTopRightRadius: '12px', flexShrink: 0 }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--color-text-main)' }}>{delivery ? 'Edit Delivery' : 'Add Delivery'}</h2>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '0.25rem' }}>
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>

            {/* 1. Party / Customer Details Section */}
            <div style={{ gridColumn: 'span 3', marginBottom: '0.25rem' }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: '700', color: 'var(--color-primary)', margin: 0 }}>1. Party / Customer Details</h3>
            </div>

            {/* Row 1: Select Lead, Party / Customer Name, Phone Number */}
            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Select Lead (Optional)</label>
              <Dropdown 
                searchable={true}
                placeholder="Select a Lead"
                value={formData.leadId}
                onChange={(val) => handleDropdownChange('leadId', val)}
                options={leads.map(lead => ({ value: lead.id, label: `${lead.companyName} (${lead.contactPerson})` }))}
                style={{ pointerEvents: delivery ? 'none' : 'auto', opacity: delivery ? 0.7 : 1 }}
              />
            </div>

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Party / Customer Name <span style={{color: "var(--color-danger)"}}>*</span></label>
              <input type="text" name="partyName" value={formData.partyName} onChange={handleChange} className="input-field" placeholder="Party Name" style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--color-border)', borderRadius: '12px' }} />
            </div>

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Phone Number</label>
              <input type="text" name="partyPhone" value={formData.partyPhone} onChange={handleChange} className="input-field" placeholder="Phone / Contact No" style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--color-border)', borderRadius: '12px' }} />
            </div>

            {/* Row 2: Email Address, Delivery Title, Delivery Address */}
            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Email Address <span className="text-danger">*</span></label>
              <input type="email" name="partyEmail" value={formData.partyEmail} onChange={handleChange} className="input-field" placeholder="Email Address" style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--color-border)', borderRadius: '12px' }} />
            </div>

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Delivery Title / Item Name <span style={{color: "var(--color-danger)"}}>*</span></label>
              <input type="text" name="title" value={formData.title} onChange={handleChange} className="input-field" placeholder="E.g. Sample Product Delivery" style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--color-border)', borderRadius: '12px' }} />
            </div>

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Delivery Address <span className="text-danger">*</span></label>
              <input type="text" name="partyAddress" value={formData.partyAddress} onChange={handleChange} className="input-field" placeholder="Street Address, Building, Area" style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--color-border)', borderRadius: '12px' }} />
            </div>

            {/* Row 3: City, State, Pincode */}
            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>City</label>
              <input type="text" name="partyCity" value={formData.partyCity} onChange={handleChange} className="input-field" placeholder="City" style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--color-border)', borderRadius: '12px' }} />
            </div>

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>State</label>
              <input type="text" name="partyState" value={formData.partyState} onChange={handleChange} className="input-field" placeholder="State" style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--color-border)', borderRadius: '12px' }} />
            </div>

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Pincode</label>
              <input type="text" name="partyPincode" value={formData.partyPincode} onChange={handleChange} className="input-field" placeholder="Pincode" style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--color-border)', borderRadius: '12px' }} />
            </div>

            
            {/* View GPS Location on Map if available */}
            {(formData.latitude && formData.longitude) && (
              <div style={{ gridColumn: 'span 3', marginTop: '0.25rem', padding: '0.85rem 1rem', backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#0369a1', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <MapPin size={16} color="#0284c7" /> Location Captured
                </span>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${formData.latitude},${formData.longitude}`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn"
                  style={{ textDecoration: 'none', padding: '0.3rem 0.75rem', fontSize: '0.78rem', backgroundColor: '#0284c7', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' }}
                >
                  🗺️ View in Google Maps
                </a>
              </div>
            )}
            
            {/* 2. Order & Payment Info Section */}
            <div style={{ gridColumn: 'span 3', marginTop: '0.5rem', marginBottom: '0.25rem', paddingTop: '0.75rem', borderTop: '1px solid #e2e8f0' }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: '700', color: 'var(--color-primary)', margin: 0 }}>2. Order & Payment Info</h3>
            </div>

            {/* Row 4: Order No, Order Date, Payment Amount (₹) */}
            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Order No</label>
              <input type="text" name="orderNo" value={formData.orderNo} onChange={handleChange} className="input-field" placeholder="E.g. ORD-1002" style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--color-border)', borderRadius: '12px' }} />
            </div>

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Order Date</label>
              <input type="date" name="orderDate" value={formData.orderDate} onChange={handleChange} className="input-field" style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--color-border)', borderRadius: '12px' }} />
            </div>

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Payment Amount (₹)</label>
              <input type="number" step="0.01" name="amount" value={formData.amount} onChange={handleChange} className="input-field" placeholder="E.g. 1500.00" style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--color-border)', borderRadius: '12px' }} />
            </div>

            {/* 3. Assignment & Status Section */}
            <div style={{ gridColumn: 'span 3', marginTop: '0.5rem', marginBottom: '0.25rem', paddingTop: '0.75rem', borderTop: '1px solid #e2e8f0' }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: '700', color: 'var(--color-primary)', margin: 0 }}>3. Assignment & Status</h3>
            </div>

            {/* Row 5: Assign Delivery Boy, Scheduled Date, Status */}
            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Assign Delivery Boy <span className="text-danger">*</span></label>
              <Dropdown 
                searchable={true}
                placeholder="Unassigned"
                value={formData.deliveryBoyId}
                onChange={(val) => handleDropdownChange('deliveryBoyId', val)}
                options={[{value: '', label: 'Unassigned'}, ...users.map(user => ({ value: user.id, label: `${user.name} (${user.role?.name || user.role || 'User'})` }))]}
              />
            </div>

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Scheduled Date & Time <span className="text-danger">*</span></label>
              <input type="datetime-local" name="scheduledDate" value={formData.scheduledDate} onChange={handleChange} className="input-field" style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--color-border)', borderRadius: '12px' }} />
            </div>

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Status</label>
              <Dropdown 
                placeholder="Select Status"
                value={formData.status}
                onChange={(val) => handleDropdownChange('status', val)}
                options={statusOptions}
              />
            </div>

            {/* Row 6: Details / Notes */}
            <div className="form-group" style={{ gridColumn: 'span 3' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Details / Notes</label>
              <textarea name="details" value={formData.details} onChange={handleChange} className="input-field" rows="3" style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--color-border)', borderRadius: '12px', resize: 'vertical' }} placeholder="Any instructions for delivery..."></textarea>
            </div>
          </div>

          <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '2rem' }}>
            <button type="button" onClick={onClose} className="btn" style={{ padding: '0.5rem 1.5rem', border: '1px solid var(--color-border)', borderRadius: '12px', background: 'white', cursor: 'pointer', fontWeight: '500' }}>Cancel</button>
            <button type="submit" disabled={loading} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1.5rem', border: 'none', borderRadius: '12px', background: 'var(--color-primary)', color: 'white', cursor: 'pointer', fontWeight: '500' }}>
              {loading && <Loader2 size={16} className="spinner-icon" />}
              {loading ? 'Saving...' : 'Save Delivery'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDelivery;
